import React from 'react';
import { Card, Button, Alert, Tag, Space } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { AgentProposal } from '../../types/agent';
import { isTierB } from '../../types/agent';
import styles from './ActionProposalCard.module.css';

export type ProposalStatus = 'pending' | 'executing' | 'done' | 'failed';

interface ActionProposalCardProps {
  proposal: AgentProposal;
  status: ProposalStatus;
  onConfirm: () => void;
  onAllowAlways?: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  id: 'Ідентифікатор',
  category_id: 'Категорія',
  name: 'Назва',
  price_per_kg: 'Ціна (грн/кг)',
  description: 'Опис',
  is_active: 'Активний',
  is_featured: 'Рекомендований',
  question: 'Запитання',
  answer: 'Відповідь',
};

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] || key;
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'так' : 'ні';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const ActionProposalCard: React.FC<ActionProposalCardProps> = ({
  proposal,
  status,
  onConfirm,
  onAllowAlways,
}) => {
  const tierB = isTierB(proposal);
  const payloadEntries = Object.entries(proposal.payload || {});
  const canAllowAlways = !tierB && !!proposal.action_key && !!onAllowAlways;

  return (
    <Card
      size="small"
      className={styles.card}
      title={
        <span>
          {proposal.action_label || 'Пропозиція дії'}{' '}
          <Tag color={tierB ? 'warning' : 'blue'}>Tier {proposal.tier}</Tag>
        </span>
      }
    >
      {tierB && (
        <Alert
          className={styles.warning}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Чутлива дія"
          description={proposal.scope_summary}
        />
      )}

      {!tierB && <div className={styles.summary}>{proposal.scope_summary}</div>}

      {payloadEntries.length > 0 && (
        <div className={styles.payload}>
          {payloadEntries.map(([k, v]) => (
            <div key={k} className={styles.payloadRow}>
              <span className={styles.payloadKey}>{fieldLabel(k)}</span>
              <span className={styles.payloadVal}>{renderValue(v)}</span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.meta}>
        {proposal.method} {proposal.endpoint}
      </div>

      <div className={styles.footer}>
        {status === 'pending' && (
          <Space wrap>
            <Button type="primary" onClick={onConfirm}>
              Дозволити раз
            </Button>
            {canAllowAlways && (
              <Button onClick={onAllowAlways}>Дозволяти завжди</Button>
            )}
          </Space>
        )}
        {status === 'executing' && (
          <Button type="primary" loading disabled>
            Виконується…
          </Button>
        )}
        {status === 'done' && (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Виконано
          </Tag>
        )}
        {status === 'failed' && <Tag color="error">Помилка</Tag>}
      </div>
    </Card>
  );
};

export default ActionProposalCard;
